import React, { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import styled, { keyframes } from 'styled-components'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import CloseIcon from '@mui/icons-material/Close'
import {
  selectSelectedTaskIds,
  selectBulkActionLoading,
  selectProjects,
  selectTasks,
  selectTeams
} from '../ducks/selectors'
import {
  bulkDeleteTasksRoutine,
  bulkUpdateTasksRoutine,
  bulkSignOffRoutine,
  clearSelectionRoutine,
  getTasksRoutine
} from '../ducks/actions'
import BulkAssignPanel from './BulkAssignPanel'
import FormTemplateField from './FormTemplateField'
import { toast } from 'react-toastify'
import { PRIORITY_META } from 'features/taskManagement/taskPriority'

const statusOptions = [
  { value: 'unassigned', label: 'Unassigned' },
  { value: 'assigned', label: 'Assigned' },
  { value: 'in_progress', label: 'In Progress' }
]

const priorityOptions = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: PRIORITY_META.critical.label }
]

const BulkActionsBar = () => {
  const dispatch = useDispatch()
  const selectedTaskIds = useSelector(selectSelectedTaskIds)
  const bulkLoading = useSelector(selectBulkActionLoading)
  const projects = useSelector(selectProjects)
  const teams = useSelector(selectTeams)
  const allTasks = useSelector(selectTasks)
  const [bulkSetObligationDefault, setBulkSetObligationDefault] = useState(false)

  const selectedTasks = allTasks.filter(t => selectedTaskIds.includes(t.id))
  const isObligationLinked = (t) => !!(t.metadata?.programmeObligationId || t.metadata?.programme_obligation_id)
  const canUpdateCount = selectedTasks.filter(t => t.permissions?.canUpdate).length
  const canAssignCount = selectedTasks.filter(t => t.permissions?.canAssign).length
  const canDeleteCount = selectedTasks.filter(t => t.permissions?.canDelete).length
  const canSignOffCount = selectedTasks.filter(t => t.permissions?.canSignOff && t.status === 'completed' && !t.signedOffAt).length
  const obligationLinkedCount = selectedTasks.filter(isObligationLinked).length
  const formChangeCount = selectedTasks.filter(t => t.permissions?.canUpdate && !isObligationLinked(t)).length

  const [openMenu, setOpenMenu] = useState(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [assignPanel, setAssignPanel] = useState(null)
  const [formModalOpen, setFormModalOpen] = useState(false)
  const [bulkFormId, setBulkFormId] = useState('')
  const [bulkFormTemplate, setBulkFormTemplate] = useState(null)
  const [currentFormId, setCurrentFormId] = useState(null)
  const menuRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenu(null)
        setShowDeleteConfirm(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  if (!selectedTaskIds.length) return null

  const handleBulkUpdate = (fields) => {
    dispatch(bulkUpdateTasksRoutine({ taskIds: selectedTaskIds, ...fields }))
    setOpenMenu(null)
  }

  const openFormModal = () => {
    const changeable = selectedTasks.filter(t => t.permissions?.canUpdate && !isObligationLinked(t))
    const ids = changeable.map(t => t.formTemplate?.id || null)
    const sharedId = ids.length > 0 && ids.every(id => id === ids[0]) ? ids[0] : null
    if (sharedId) {
      setBulkFormId(sharedId)
      setBulkFormTemplate(changeable.find(t => t.formTemplate?.id === sharedId)?.formTemplate || null)
      setCurrentFormId(sharedId)
    } else {
      setBulkFormId('')
      setBulkFormTemplate(null)
      setCurrentFormId(null)
    }
    setFormModalOpen(true)
  }

  const applyBulkFormChange = () => {
    if (!bulkFormId) { toast.info('Choose a form first.'); return }
    dispatch(bulkUpdateTasksRoutine({ taskIds: selectedTaskIds, formTemplateId: bulkFormId }))
    setFormModalOpen(false)
    setBulkFormId('')
    setBulkFormTemplate(null)
    setCurrentFormId(null)
  }

  const openAssignPanel = (kind) => {
    setOpenMenu(null)
    setAssignPanel(kind)
  }

  const handleAssignPanelDone = () => {
    setAssignPanel(null)
    dispatch(clearSelectionRoutine())
    dispatch(getTasksRoutine())
  }

  const handleBulkDelete = () => {
    dispatch(bulkDeleteTasksRoutine({ taskIds: selectedTaskIds }))
    setShowDeleteConfirm(false)
    setOpenMenu(null)
  }

  const handleBulkSignOff = () => {
    const signOffableIds = selectedTasks
      .filter(t => t.permissions?.canSignOff && t.status === 'completed' && !t.signedOffAt)
      .map(t => t.id)
    dispatch(bulkSignOffRoutine({ taskIds: signOffableIds }))
    setOpenMenu(null)
  }

  const handleDeselect = () => {
    dispatch(clearSelectionRoutine())
  }

  return (
    <BarWrapper ref={menuRef}>
      <BarContent>
        <LeftSection>
          <SelectedCount>{selectedTaskIds.length} task{selectedTaskIds.length > 1 ? 's' : ''} selected</SelectedCount>
          <DeselectLink onClick={handleDeselect}>Deselect all</DeselectLink>
        </LeftSection>

        <RightSection>
          <ActionButton
            disabled={bulkLoading || canUpdateCount === 0}
            title={canUpdateCount === 0 ? 'No permission to update selected tasks' : `Can update ${canUpdateCount} of ${selectedTaskIds.length}`}
            onClick={() => setOpenMenu(openMenu === 'status' ? null : 'status')}
          >
            Status
            {openMenu === 'status' && (
              <ActionMenu>
                {statusOptions.map(opt => (
                  <MenuItem key={opt.value} onClick={(e) => {
                    e.stopPropagation()
                    handleBulkUpdate({ status: opt.value })
                  }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </ActionMenu>
            )}
          </ActionButton>

          <ActionButton
            disabled={bulkLoading || canUpdateCount === 0}
            title={canUpdateCount === 0 ? 'No permission to update selected tasks' : `Can update ${canUpdateCount} of ${selectedTaskIds.length}`}
            onClick={() => setOpenMenu(openMenu === 'priority' ? null : 'priority')}
          >
            Priority
            {openMenu === 'priority' && (
              <ActionMenu>
                {priorityOptions.map(opt => (
                  <MenuItem key={opt.value} onClick={(e) => {
                    e.stopPropagation()
                    handleBulkUpdate({ priority: opt.value })
                  }}>
                    {opt.label}
                  </MenuItem>
                ))}
              </ActionMenu>
            )}
          </ActionButton>

          <ActionButton
            disabled={bulkLoading || canUpdateCount === 0}
            title={canUpdateCount === 0 ? 'No permission to update selected tasks' : `Can update ${canUpdateCount} of ${selectedTaskIds.length}`}
            onClick={() => setOpenMenu(openMenu === 'project' ? null : 'project')}
          >
            Move to Project
            {openMenu === 'project' && (
              <ActionMenu>
                {obligationLinkedCount > 0 && (
                  <MenuCheck onClick={(e) => e.stopPropagation()}>
                    <input
                      type='checkbox'
                      checked={bulkSetObligationDefault}
                      onChange={(e) => setBulkSetObligationDefault(e.target.checked)}
                    />
                    Set as the default for their obligations
                  </MenuCheck>
                )}
                <MenuItem onClick={(e) => {
                  e.stopPropagation()
                  handleBulkUpdate({ taskProjectId: null, setObligationDefault: bulkSetObligationDefault })
                }}>
                  No Project
                </MenuItem>
                {projects.map(p => (
                  <MenuItem key={p.id} onClick={(e) => {
                    e.stopPropagation()
                    handleBulkUpdate({ taskProjectId: p.id, setObligationDefault: bulkSetObligationDefault })
                  }}>
                    {p.name}
                  </MenuItem>
                ))}
              </ActionMenu>
            )}
          </ActionButton>

          <ActionButton
            disabled={bulkLoading || canUpdateCount === 0}
            title={canUpdateCount === 0 ? 'No permission to update selected tasks' : `Assign a team to ${canUpdateCount} of ${selectedTaskIds.length}`}
            onClick={() => setOpenMenu(openMenu === 'team' ? null : 'team')}
          >
            Assign team
            {openMenu === 'team' && (
              <ActionMenu onClick={(e) => e.stopPropagation()}>
                {obligationLinkedCount > 0 && (
                  <MenuCheck>
                    <input
                      type='checkbox'
                      checked={bulkSetObligationDefault}
                      onChange={(e) => setBulkSetObligationDefault(e.target.checked)}
                    />
                    Set as the default for their obligations
                  </MenuCheck>
                )}
                <MenuItem onClick={() => handleBulkUpdate({ taskTeamId: null, setObligationDefault: bulkSetObligationDefault })}>
                  No team
                </MenuItem>
                {teams.length === 0 && <MenuNote>No teams yet</MenuNote>}
                {teams.map(t => (
                  <MenuItem key={t.id} onClick={() => handleBulkUpdate({ taskTeamId: t.id, setObligationDefault: bulkSetObligationDefault })}>
                    {t.name}
                  </MenuItem>
                ))}
              </ActionMenu>
            )}
          </ActionButton>

          <ActionButton
            disabled={bulkLoading || canAssignCount === 0}
            title={canAssignCount === 0
              ? 'You do not have permission to assign any of the tasks you picked'
              : `You can assign ${canAssignCount} of ${selectedTaskIds.length}`}
            onClick={() => openAssignPanel('assignee')}
            data-tour-id='tasks.bulk-assign-people'
          >
            Assign people
          </ActionButton>

          <ActionButton
            disabled={bulkLoading || canAssignCount === 0}
            title={canAssignCount === 0
              ? 'You do not have permission to assign any of the tasks you picked'
              : `You can set approvers on ${canAssignCount} of ${selectedTaskIds.length}`}
            onClick={() => openAssignPanel('approver')}
          >
            Approvers
          </ActionButton>

          <ActionButton
            disabled={bulkLoading || canAssignCount === 0}
            title={canAssignCount === 0
              ? 'You do not have permission to assign any of the tasks you picked'
              : 'Give people outside your organisation access to these tasks'}
            onClick={() => openAssignPanel('external')}
          >
            Outside people
          </ActionButton>

          <ActionButton
            disabled={bulkLoading || canUpdateCount === 0}
            title={canUpdateCount === 0
              ? 'You do not have permission to change any of the tasks you picked'
              : `You can set a contractor on ${canUpdateCount} of ${selectedTaskIds.length}`}
            onClick={() => openAssignPanel('contractor')}
          >
            Contractor
          </ActionButton>

          <ActionButton
            disabled={bulkLoading || canUpdateCount === 0}
            title={
              canUpdateCount === 0
                ? 'No permission to update selected tasks'
                : formChangeCount === 0
                  ? 'Obligation-linked task forms can’t be changed in bulk'
                  : 'Change the form on the selected tasks'
            }
            onClick={openFormModal}
          >
            Change form
          </ActionButton>

          {canSignOffCount > 0 && (
            <SignOffButton
              disabled={bulkLoading}
              title={`Sign off ${canSignOffCount} completed task${canSignOffCount !== 1 ? 's' : ''}`}
              onClick={handleBulkSignOff}
            >
              <AssignmentTurnedInOutlinedIcon />
              Sign Off ({canSignOffCount})
            </SignOffButton>
          )}

          <DeleteButton
            disabled={bulkLoading || canDeleteCount === 0}
            title={canDeleteCount === 0 ? 'No permission to delete selected tasks' : `Can delete ${canDeleteCount} of ${selectedTaskIds.length}`}
            onClick={() => setShowDeleteConfirm(true)}
          >
            <DeleteOutlineIcon />
            Delete
            {showDeleteConfirm && (
              <ConfirmPopup onClick={(e) => e.stopPropagation()}>
                <ConfirmText>
                  Delete {canDeleteCount} of {selectedTaskIds.length} selected task{selectedTaskIds.length > 1 ? 's' : ''}?
                </ConfirmText>
                <ConfirmActions>
                  <ConfirmCancel onClick={() => setShowDeleteConfirm(false)}>Cancel</ConfirmCancel>
                  <ConfirmDelete onClick={handleBulkDelete}>Delete</ConfirmDelete>
                </ConfirmActions>
              </ConfirmPopup>
            )}
          </DeleteButton>
        </RightSection>

        <CloseBar onClick={handleDeselect}>
          <CloseIcon />
        </CloseBar>
      </BarContent>

      {assignPanel && (
        <BulkAssignPanel
          kind={assignPanel}
          taskIds={selectedTaskIds}
          onClose={() => setAssignPanel(null)}
          onDone={handleAssignPanelDone}
        />
      )}

      {formModalOpen && createPortal(
        <FormModalOverlay onClick={() => setFormModalOpen(false)}>
          <FormModalCard onClick={(e) => e.stopPropagation()}>
            {formChangeCount === 0 ? (
              <>
                <FormModalTitle>You can’t change these forms in bulk</FormModalTitle>
                <FormModalWarn role='alert'>
                  Every task you’ve selected is linked to a compliance obligation, and an obligation’s form can’t be changed in bulk. Open a task on its own to change its obligation’s form. That updates the obligation and all its future tasks.
                </FormModalWarn>
                <FormModalActions>
                  <FormApplyBtn onClick={() => setFormModalOpen(false)}>Got it</FormApplyBtn>
                </FormModalActions>
              </>
            ) : (
              <>
                <FormModalTitle>Change the form on {formChangeCount} task{formChangeCount !== 1 ? 's' : ''}</FormModalTitle>
                <FormModalWarn role='alert'>
                  This replaces the form on those tasks and <strong>clears any answers already entered</strong> on them. This can’t be undone.
                  {obligationLinkedCount > 0 && (
                    <> The {obligationLinkedCount} obligation-linked task{obligationLinkedCount !== 1 ? 's' : ''} in your selection <strong>can’t be changed in bulk</strong> and will be left as {obligationLinkedCount !== 1 ? 'they are' : 'it is'}. An obligation’s form must be changed one task at a time.</>
                  )}
                </FormModalWarn>
                <FormTemplateField
                  value={bulkFormId}
                  template={bulkFormTemplate}
                  onChange={(tpl) => { setBulkFormId(tpl?.id || ''); setBulkFormTemplate(tpl || null) }}
                  emptyLabel='No form chosen yet'
                  emptyHint='Pick the form these tasks should use.'
                />
                <FormModalActions>
                  <ConfirmCancel onClick={() => setFormModalOpen(false)}>Cancel</ConfirmCancel>
                  <FormApplyBtn disabled={!bulkFormId || bulkLoading || bulkFormId === currentFormId} onClick={applyBulkFormChange}>
                    {bulkFormId && bulkFormId === currentFormId ? 'Pick a different form' : 'Change form & clear answers'}
                  </FormApplyBtn>
                </FormModalActions>
              </>
            )}
          </FormModalCard>
        </FormModalOverlay>,
        document.body
      )}
    </BarWrapper>
  )
}

export default BulkActionsBar

const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`

const BarWrapper = styled.div`
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1000;
  animation: ${slideUp} 0.2s ease-out;
  max-width: calc(100vw - 32px);

  @media (max-width: 768px) {
    bottom: var(--bp-float-1);
    left: 16px;
    right: 16px;
    transform: none;
    max-width: none;
  }
`

const BarContent = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  background: ${({ theme }) => theme.colors.haiti};
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.24);
  color: white;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 10px;
    padding: 10px 14px;
  }
`

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  border-right: 1px solid rgba(255, 255, 255, 0.2);
  padding-right: 16px;

  @media (max-width: 768px) {
    border-right: none;
    padding-right: 0;
    flex: 1;
  }
`

const SelectedCount = styled.span`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
`

const DeselectLink = styled.span`
  font-size: 12px;
  opacity: 0.7;
  cursor: pointer;
  white-space: nowrap;
  &:hover { opacity: 1; text-decoration: underline; }
`

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    width: 100%;
    gap: 6px;
  }
`

const ActionButton = styled.button`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  background: transparent;
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.5);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const SignOffButton = styled(ActionButton)`
  border-color: ${({ theme }) => theme.colors.success};
  color: ${({ theme }) => theme.colors.success};
  svg { font-size: 16px; }

  &:hover:not(:disabled) {
    background: ${({ theme }) => `${theme.colors.success}33`};
    border-color: ${({ theme }) => theme.colors.success};
  }
`

const DeleteButton = styled(ActionButton)`
  border-color: ${({ theme }) => theme.colors.error};
  color: ${({ theme }) => `${theme.colors.error}cc`};
  svg { font-size: 16px; }

  &:hover:not(:disabled) {
    background: ${({ theme }) => `${theme.colors.error}33`};
    border-color: ${({ theme }) => theme.colors.error};
  }
`

const ActionMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 0;
  min-width: ${({ $wide }) => ($wide ? '220px' : '160px')};
  max-height: 200px;
  overflow-y: auto;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);
  padding: 4px;
  z-index: 10;

  @media (max-width: 768px) {
    min-width: 160px;
    left: auto;
    right: 0;
  }
`

const MenuItem = styled.div`
  padding: 8px 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};
  border-radius: 6px;
  cursor: pointer;
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`

const MenuNote = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
`

const MenuCheck = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#475569'};
  cursor: pointer;
  border-bottom: 1px solid #eef0f3;

  input { margin-top: 1px; accent-color: #33becc; cursor: pointer; }
`

const ConfirmPopup = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
  background: white;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.16);
  padding: 16px;
  z-index: 10;
  min-width: 200px;
`

const ConfirmText = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 12px;
`

const ConfirmActions = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`

const ConfirmCancel = styled.button`
  padding: 6px 12px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrey};
  border-radius: 6px;
  background: white;
  font-size: 12px;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.grey?.[800] || '#999'};
  &:hover { background: ${({ theme }) => theme.colors.lightGrey}; }
`

const ConfirmDelete = styled.button`
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.error};
  color: white;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`

const CloseBar = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 2px;
  margin-left: 4px;
  svg { font-size: 18px; }
  &:hover { color: white; }
`

const FormModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.55);
`

const FormModalCard = styled.div`
  width: 100%;
  max-width: 560px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.25);
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (max-width: 480px) {
    padding: 16px;
  }
`

const FormModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const FormModalWarn = styled.p`
  margin: 0;
  padding: 12px 14px;
  font-size: 13px;
  line-height: 1.55;
  color: #7c2d12;
  background: #fff7ed;
  border: 1px solid #ffedd5;
  border-radius: 8px;
`

const FormModalActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 4px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    & > * { width: 100%; }
  }
`

const FormApplyBtn = styled.button`
  padding: 9px 16px;
  border: none;
  border-radius: 8px;
  background: #c2410c;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`
